/**
 * The Onboarding and Offboarding screens: one list each, open ones first --
 * how far along, how many cards are late, and how many are held by somebody
 * who has gone.
 */

import type { InstanceSummary, LifecycleKind } from "@utuwrk/core";
import { useOrg } from "../lib/org.js";
import { boardOf } from "../lib/router.js";
import { anchorLabel, dayText, kindWord, plural, words } from "../lib/words.js";
import { EmptyState, Link, Loading, PageHeader, Problem, useLoad } from "./bits.js";
import { Icon } from "./Icon.js";

export const canStartHere = (role: string): boolean => role === "admin" || role === "hr";

/** A native progress bar: the CSP allows no inline styles, and screen readers already know this element. */
export function Progress({ instance }: { instance: InstanceSummary }) {
  return (
    <progress
      className="progress"
      max={Math.max(instance.total, 1)}
      value={instance.finished}
      aria-label={`${instance.finished} of ${instance.total} cards finished`}
    />
  );
}

/** What each list says, so the two read as two screens rather than one with a filter. */
const LIST = {
  onboarding: {
    title: "Onboarding",
    icon: "user-plus",
    empty: "Nobody is starting right now",
    hint: "Start an onboarding when an offer is accepted.",
    start: { name: "start" },
    button: "Start an onboarding",
  },
  offboarding: {
    title: "Offboarding",
    icon: "user-minus",
    empty: "Nobody is leaving right now",
    hint: "Start an offboarding when somebody resigns, is let go, reaches the end of a contract, or stays but loses some access.",
    start: { name: "leave" },
    button: "Start an offboarding",
  },
} as const;

/** Every onboarding, or every offboarding, in the organisation: open ones first, finished ones folded away. */
export function Onboardings({ kind }: { kind: LifecycleKind }) {
  const { api, org } = useOrg();
  const { data, error } = useLoad(() => api.instances(), [api]);
  const list = LIST[kind];

  if (!data && !error) return <Loading title={list.title} />;
  const mine = data?.instances.filter((instance) => instance.kind === kind) ?? [];
  const open = mine.filter((instance) => instance.closedAt === null);
  const closed = mine.filter((instance) => instance.closedAt !== null);

  return (
    <section className="page">
      <PageHeader
        title={list.title}
        actions={
          // Start onboarding is already in the sidebar and the phone's tab bar; only offboarding needs a door here.
          kind === "offboarding" && canStartHere(org.role) ? (
            <Link to={list.start} className="button">
              <Icon name="plus" size={18} />
              {list.button}
            </Link>
          ) : null
        }
      />
      <Problem>{error}</Problem>

      {data ? (
        <>
          {open.length === 0 ? (
            <EmptyState icon={list.icon} title={list.empty}>
              {canStartHere(org.role) ? list.hint : null}
            </EmptyState>
          ) : (
            <ul className="cards">
              {open.map((instance) => (
                <InstanceRow key={instance.id} instance={instance} />
              ))}
            </ul>
          )}
          {closed.length > 0 ? (
            <details className="panel zone-done">
              <summary>Finished: {plural(closed.length, kind)}</summary>
              <ul className="cards">
                {closed.map((instance) => (
                  <InstanceRow key={instance.id} instance={instance} />
                ))}
              </ul>
            </details>
          ) : null}
        </>
      ) : null}
    </section>
  );
}

/**
 * One onboarding (or offboarding) as a link to its board: here, under the
 * person's name, and on an employee's page, where it is named by its template.
 */
export function InstanceRow({ instance, byTemplate }: { instance: InstanceSummary; byTemplate?: boolean }) {
  return (
    <li>
      <Link to={boardOf(instance.id, instance.kind)} className="card card-link">
        <div className="card-top">
          <h2 className="card-title">{byTemplate ? instance.templateName : instance.employeeName}</h2>
          <span className="card-count">
            {instance.finished} of {instance.total}
          </span>
        </div>
        {instance.cancelled ? null : <Progress instance={instance} />}
        <p className="card-meta">
          <span>
            {instance.kind === "offboarding" ? anchorLabel(instance) : "Starts"}{" "}
            {dayText(instance.anchorDate)}
          </span>
          <span className="chip">{instance.siteName}</span>
          {byTemplate ? (
            // On an employee's page both kinds sit together; on the two lists the screen says which.
            <span className={`pill ${instance.kind === "offboarding" ? "s-warn" : "s-progress"}`}>
              {words(kindWord(instance))}
            </span>
          ) : null}
          {byTemplate ? null : <span>{instance.templateName}</span>}
          {instance.overdue > 0 ? <span className="pill s-danger">{instance.overdue} late</span> : null}
          {instance.flagged > 0 ? (
            <span className="pill s-warn">{plural(instance.flagged, "card")} to hand over</span>
          ) : null}
          {instance.cancelled ? <span className="pill s-waiting">Cancelled</span> : null}
        </p>
      </Link>
    </li>
  );
}
